import type { ListenerAudio } from '../../../hooks/useListenerAudio';
import type { Resource } from '../../../hooks/useResources';
import type {
  CollectionItem,
  CollectionItemType,
  CollectionItemUpdate,
  ContentCollection,
} from '../../../hooks/useContentCollections';
import type { AddableCollectionItem } from '../../../components/content-library/add-collection-item-modal';

/** Format collection relationship for display using clients[], domain, and verticals from API response */
export const formatCollectionRelationship = (collection: ContentCollection): string => {
  const clients =
    collection.clients && collection.clients.length > 0
      ? collection.clients
      : collection.client_id
        ? [{ id: collection.client_id, name: collection.client_name }]
        : [];
  if (clients.length === 1) {
    const name = clients[0].name || `#${clients[0].id}`;
    return `Client: ${name}`;
  }
  if (clients.length > 1) {
    const names = clients.map((c) => c.name || `#${c.id}`);
    if (names.length <= 3) {
      return `Clients: ${names.join(', ')}`;
    }
    return `Clients: ${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
  }
  const domainName = collection.domain_name ?? collection.domain?.name;
  const verticalNames = collection.verticals?.map((v) => v.name).filter(Boolean) ?? [];
  if (domainName && verticalNames.length > 0) {
    return `Domain: ${domainName}, Vertical: ${verticalNames.join(', ')}`;
  }
  if (domainName) {
    return `Domain: ${domainName}`;
  }
  if (verticalNames.length > 0) {
    return `Vertical: ${verticalNames.join(', ')}`;
  }
  return '—';
};

export const getCollectionClientIds = (collection: ContentCollection): number[] => {
  if (collection.clients?.length) {
    return collection.clients.map((c) => c.id);
  }
  return collection.client_id != null ? [collection.client_id] : [];
};

const mapItemToUpdatePayload = (item: CollectionItem, sortOrder: number): CollectionItemUpdate | null => {
  // Include item.id to preserve existing items (and their created_at timestamps)
  if (item.resource_id) {
    return { id: item.id, resource_id: item.resource_id, sort_order: sortOrder };
  }
  if (item.listener_audio_id) {
    return { id: item.id, listener_audio_id: item.listener_audio_id, sort_order: sortOrder };
  }
  if (item.listener_role_id) {
    return { id: item.id, listener_role_id: item.listener_role_id, sort_order: sortOrder };
  }
  if (item.chatbot_id) {
    return { id: item.id, chatbot_id: item.chatbot_id, sort_order: sortOrder };
  }
  return null;
};

export const buildItemsPayload = (items: CollectionItem[]): CollectionItemUpdate[] => {
  return items
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((item, index) => mapItemToUpdatePayload(item, index))
    .filter((payload): payload is CollectionItemUpdate => payload !== null);
};

export const selectionToUpdatePayload = (selection: AddableCollectionItem, sortOrder: number): CollectionItemUpdate => {
  if (selection.type === 'resource') {
    if (selection.resource.id === null || selection.resource.id === undefined) {
      throw new Error('Resource is missing an id');
    }
    return { resource_id: selection.resource.id, sort_order: sortOrder };
  }
  return { listener_audio_id: selection.experience.id, sort_order: sortOrder };
};

export const formatCollectionItemType = (type: CollectionItemType): string => {
  switch (type) {
    case 'audio':
      return 'Audio';
    case 'video':
      return 'Video';
    case 'resource':
      return 'Resource';
    case 'listener':
      return 'Listener';
    case 'chatbot':
      return 'Chatbot';
    default:
      return 'Item';
  }
};

export const getCollectionItemSummary = (item: CollectionItem): { title: string; subtitle?: string } => {
  if (item.contentType === 'resource') {
    const resource = item.data as Resource | undefined;
    return {
      title: resource?.name ?? `Resource #${item.resource_id ?? item.id}`,
      subtitle: `${resource?.client_id ? `${resource?.client_id}: ${resource?.client_name}` : 'Global'} <br/> ${resource?.url} <br/> Sort Weight: ${resource?.sort_weight}`,
    };
  }
  if (item.contentType === 'audio' || item.contentType === 'video') {
    const experience = item.data as ListenerAudio | undefined;
    const listenerName = experience?.listener_role?.user?.display_name || experience?.listener_role?.user?.first_name;
    return {
      title: experience?.title || `Experience #${item.listener_audio_id ?? item.id}`,
      subtitle: listenerName ? `Listener: ${listenerName}` : undefined,
    };
  }
  if (item.contentType === 'listener') {
    const listener = item.data as any;
    const name = listener?.user?.display_name || listener?.user?.first_name;
    return {
      title: name ? `Listener ${name}` : `Listener #${item.listener_role_id ?? item.id}`,
      subtitle: listener?.user?.email_address,
    };
  }
  if (item.contentType === 'chatbot') {
    const chatbot = item.data as any;
    return {
      title: chatbot?.name ?? `Chatbot #${item.chatbot_id ?? item.id}`,
      subtitle: chatbot?.status ? `Status: ${chatbot.status}` : undefined,
    };
  }
  return { title: `Item #${item.id}` };
};

export const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
};

export const getCollectionItemDetailTitle = (item: CollectionItem): string => {
  const identifier = item.resource_id ?? item.listener_audio_id ?? item.listener_role_id ?? item.chatbot_id ?? item.id;
  return `${formatCollectionItemType(item.contentType)} ${identifier}`;
};
